import { useTranslation } from 'react-i18next';
import { DownloadSimple, UploadSimple, FloppyDisk } from '@phosphor-icons/react';
import type { LatestVersionDto } from '@monorepo/shared';
import { DocumentFileIcon } from './DocumentFileIcon';
import { ParentPathBreadcrumb } from './ParentPathBreadcrumb';

interface LatestVersionsTableProps {
  versions: LatestVersionDto[];
  isLoading: boolean;
  canReupload: boolean;
  isDownloading: boolean;
  onDownload: (version: LatestVersionDto) => void;
  onReupload: (documentId: string, name: string) => void;
  onNavigateToFolder: (folderId: string) => void;
}

export const LatestVersionsTable = ({
  versions,
  isLoading,
  canReupload,
  isDownloading,
  onDownload,
  onReupload,
  onNavigateToFolder,
}: LatestVersionsTableProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin">
          <FloppyDisk size={32} className="text-primary" />
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <FloppyDisk size={64} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">
          {t('documents.latest_versions_page.empty')}
        </h3>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table className="w-full min-w-[720px]">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.latest_versions_page.table.document')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.latest_versions_page.table.location')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.latest_versions_page.table.version')}
            </th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
              {t('documents.latest_versions_page.table.action')}
            </th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.versionId} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <DocumentFileIcon mimeType={version.mimeType} />
                  <span className="text-sm font-medium text-gray-900">{version.name}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <ParentPathBreadcrumb items={version.parentPath} onNavigate={onNavigateToFolder} />
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                  v{version.versionNumber}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onDownload(version)}
                    disabled={isDownloading}
                    className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-primary hover:bg-primary/10 rounded transition disabled:opacity-50"
                    title={t('documents.latest_versions_page.table.download')}
                  >
                    <DownloadSimple size={18} />
                  </button>
                  {canReupload && (
                    <button
                      onClick={() => onReupload(version.documentId, version.name)}
                      className="inline-flex items-center gap-1 px-3 h-8 text-sm text-white bg-primary rounded hover:bg-opacity-90 transition"
                      title={t('documents.latest_versions_page.table.reupload')}
                    >
                      <UploadSimple size={16} />
                      <span className="hidden sm:inline">
                        {t('documents.latest_versions_page.table.reupload')}
                      </span>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
